/**
 * editRows (編集バッファ) から UI が必要とする純派生を導出する hook。
 *
 * useForecastDraftEditor から async RPC cluster (保存直列化 saveRunRef /
 * save・discard) と独立な「純計算クラスタ」を切り出した seam。責務はサーバー
 * 状態でなく編集バッファの導出のみ: dirty 集合 (currentDirtyKomas /
 * bgTotalDirtyKomas / anyDirty)・BG全体ライブ合計 (bgTotalLiveSlots)・現在の
 * 表示対象 (currentTarget) とその表示スロット/ラベルを算出する。dirty 判定の
 * 意味論は `models/forecastDirtyState` (= computeForecastDiff) が SoT で、本 hook
 * はその memoization と選択対象 (currentTarget) 解決に徹する (async に依存しない)。
 */
import { useMemo } from 'react'

import type {
  RetailerDraft,
  SlotValue,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { useTranslation } from 'react-i18next'

import { retailerSlotsOf } from '~/pages/DemandForecastDaily/models/editUnits'
import {
  anyRowDirty,
  isRowDirty,
  rowDirtyKomas,
  unionDirtyKomas,
} from '~/pages/DemandForecastDaily/models/forecastDirtyState'
import {
  bgTotalBreakdownSlots,
  calcBgTotalSlots,
  protoSlotsToKoma,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import type {
  KomaSlots,
  RetailerEditRow,
  TargetScope,
  UnattributedEditRow,
} from '~/pages/DemandForecastDaily/models/types'

export type CurrentTarget =
  | { kind: 'bgTotal' }
  | { kind: 'unattributed' }
  | { kind: 'retailer'; row: RetailerEditRow }
  /** グループ別の小売の合計タブ。入力する行は無い。 */
  | {
      kind: 'retailerTotal'
      retailerBusinessCd: string
      displayName: string
      slots: KomaSlots
      leaves: RetailerEditRow[]
    }
  /**
   * グループのタブを選んだが、page の小売にその葉の行が無い (内訳と page の応答の間でモードが
   * 変わった)。小売の値を葉の値と誤読させないため、値を出さない。
   */
  | { kind: 'leafWithoutRow'; displayName: string }

const EMPTY_SLOTS: KomaSlots = Array.from({ length: 48 }, () => undefined)

type UseForecastDirtyStateParams = {
  editRows: Record<string, RetailerEditRow>
  /** 現在の page に存在する編集単位。モード変更前の未保存行は破棄用に残すが表示・集計しない。 */
  activeEditRowKeys?: ReadonlySet<string>
  /** page.bgTotalSlots (Σ小売の read-only 合計)。page 未ロード時は undefined。 */
  bgTotalSlots: SlotValue[] | undefined
  /** 確定済みの集計は、葉内訳が記録なしでもサーバー値を維持する。 */
  frozenRetailers?: readonly RetailerDraft[]
  unattributedRow?: UnattributedEditRow
  /** Read-only callers may supply slots without an editor baseline. */
  unattributedSlots?: KomaSlots | SlotValue[]
  targetScope: TargetScope
  selectedRetailerBusinessCd: string | undefined
  /** 入力する行の無い小売 (グループ別の小売の合計タブ)。編集中は葉合計、確定済みはサーバー値を出す。 */
  selectedRetailerTotalOf?: string
  /** グループのタブで入力する行の無い小売。想定欄に値を出さない。 */
  selectedLeafWithoutRowOf?: string
}

export type UseForecastDirtyStateResult = {
  editRowList: RetailerEditRow[]
  bgTotalLiveSlots: KomaSlots
  currentTarget: CurrentTarget
  displaySlots: KomaSlots
  displayLabel: string
  currentEditRow: RetailerEditRow | undefined
  currentDirtyKomas: Set<number>
  /**
   * 選択中小売の編集行が dirty か (値差 or 取込元メタ差、= `isRowDirty` の full
   * 判定)。currentDirtyKomas.size > 0 では取込元メタのみの差 (koma 差なし) を
   * 取りこぼすため別に持つ。変更破棄 (canDiscard) の唯一の判定源。BG_TOTAL
   * (複数小売) ビューは currentEditRow が undefined になるため false。
   */
  currentRowDirty: boolean
  bgTotalDirtyKomas: Set<number>
  anyDirty: boolean
}

export function useForecastDirtyState({
  editRows,
  activeEditRowKeys,
  bgTotalSlots,
  frozenRetailers,
  unattributedRow,
  unattributedSlots,
  targetScope,
  selectedRetailerBusinessCd,
  selectedRetailerTotalOf,
  selectedLeafWithoutRowOf,
}: UseForecastDirtyStateParams): UseForecastDirtyStateResult {
  const { t } = useTranslation()

  const normalizedUnattributedSlots = useMemo<KomaSlots | undefined>(() => {
    if (unattributedRow !== undefined) return unattributedRow.forecastSlots
    if (unattributedSlots === undefined) return undefined
    const first = unattributedSlots[0]
    return typeof first === 'number' || first === undefined
      ? (unattributedSlots as KomaSlots)
      : protoSlotsToKoma(unattributedSlots as SlotValue[])
  }, [unattributedRow, unattributedSlots])

  const activeEditRows = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(editRows).filter(
          ([key]) => activeEditRowKeys?.has(key) ?? true,
        ),
      ),
    [editRows, activeEditRowKeys],
  )
  const editRowList = useMemo(
    () => Object.values(activeEditRows),
    [activeEditRows],
  )

  const bgTotalLiveSlots = useMemo<KomaSlots>(() => {
    if (frozenRetailers !== undefined && bgTotalSlots !== undefined)
      return protoSlotsToKoma(bgTotalSlots)
    const retailerSlots = editRowList.map((r) => r.forecastSlots)
    const breakdownSlots = bgTotalBreakdownSlots(
      retailerSlots,
      normalizedUnattributedSlots === undefined
        ? undefined
        : normalizedUnattributedSlots,
    )
    if (retailerSlots.length === 0 && bgTotalSlots !== undefined) {
      return protoSlotsToKoma(bgTotalSlots)
    }
    return calcBgTotalSlots(breakdownSlots)
  }, [editRowList, bgTotalSlots, normalizedUnattributedSlots, frozenRetailers])

  const currentTarget = useMemo<CurrentTarget>(() => {
    if (targetScope === 'UNATTRIBUTED') return { kind: 'unattributed' }
    if (
      targetScope === 'RETAILER' &&
      selectedRetailerBusinessCd !== undefined
    ) {
      const row = activeEditRows[selectedRetailerBusinessCd]
      if (row !== undefined) return { kind: 'retailer', row }
    }
    if (targetScope === 'RETAILER' && selectedRetailerTotalOf !== undefined) {
      const frozenRetailer = frozenRetailers?.find(
        (retailer) => retailer.retailerBusinessCd === selectedRetailerTotalOf,
      )
      const slots =
        frozenRetailer === undefined
          ? retailerSlotsOf(activeEditRows, selectedRetailerTotalOf)
          : protoSlotsToKoma(frozenRetailer.forecastSlots)
      const leaves = Object.values(activeEditRows).filter(
        (r) => r.retailerBusinessCd === selectedRetailerTotalOf,
      )
      if (slots !== undefined && leaves[0] !== undefined)
        return {
          kind: 'retailerTotal',
          retailerBusinessCd: selectedRetailerTotalOf,
          displayName: leaves[0].displayName,
          slots,
          leaves,
        }
    }
    if (targetScope === 'RETAILER' && selectedLeafWithoutRowOf !== undefined) {
      const row = Object.values(activeEditRows).find(
        (r) => r.retailerBusinessCd === selectedLeafWithoutRowOf,
      )
      if (row !== undefined)
        return { kind: 'leafWithoutRow', displayName: row.displayName }
    }
    return { kind: 'bgTotal' }
  }, [
    targetScope,
    selectedRetailerBusinessCd,
    selectedRetailerTotalOf,
    selectedLeafWithoutRowOf,
    activeEditRows,
    frozenRetailers,
  ])

  const displaySlots: KomaSlots =
    currentTarget.kind === 'retailer'
      ? currentTarget.row.forecastSlots
      : currentTarget.kind === 'retailerTotal'
        ? currentTarget.slots
        : currentTarget.kind === 'leafWithoutRow'
          ? EMPTY_SLOTS
          : currentTarget.kind === 'unattributed'
            ? (normalizedUnattributedSlots ?? []).map((value) => value ?? 0)
            : bgTotalLiveSlots

  const displayLabel =
    currentTarget.kind === 'retailer'
      ? currentTarget.row.displayName
      : currentTarget.kind === 'retailerTotal' ||
          currentTarget.kind === 'leafWithoutRow'
        ? currentTarget.displayName
        : currentTarget.kind === 'unattributed'
          ? t('page:demandForecastDaily.toolbar.unattributed')
          : t('page:demandForecastDaily.toolbar.bgTotal')

  const currentEditRow =
    currentTarget.kind === 'retailer' ? currentTarget.row : undefined

  const currentDirtyKomas = useMemo<Set<number>>(
    () =>
      currentTarget.kind === 'retailerTotal'
        ? unionDirtyKomas(currentTarget.leaves)
        : currentEditRow === undefined
          ? new Set()
          : rowDirtyKomas(currentEditRow),
    [currentTarget, currentEditRow],
  )

  const currentRowDirty = useMemo<boolean>(
    () => (currentEditRow === undefined ? false : isRowDirty(currentEditRow)),
    [currentEditRow],
  )

  const bgTotalDirtyKomas = useMemo<Set<number>>(
    () =>
      unionDirtyKomas(
        unattributedRow === undefined
          ? editRowList
          : [...editRowList, unattributedRow],
      ),
    [editRowList, unattributedRow],
  )

  const anyDirty = useMemo(
    () =>
      anyRowDirty(Object.values(editRows)) ||
      (unattributedRow !== undefined && isRowDirty(unattributedRow)),
    [editRows, unattributedRow],
  )

  return {
    editRowList,
    bgTotalLiveSlots,
    currentTarget,
    displaySlots,
    displayLabel,
    currentEditRow,
    currentDirtyKomas,
    currentRowDirty,
    bgTotalDirtyKomas,
    anyDirty,
  }
}
